import throttle from 'lodash/throttle';
import type { EChartsType } from 'echarts/core';

import { PLOT_GRID_INDEX } from '@/shared/charts/renderers/echarts/time-series-chart/config/chartLayout';
import { interactionConfig } from '@/shared/charts/renderers/echarts/time-series-chart/config/interaction';
import type { TimeSeries, TimeSeriesPoint } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import {
  findNearestIndexByTime,
  findNearestIndexByValue,
} from '@/shared/charts/time-series-chart/points/findPointIndex';

import type { TooltipCard } from '../content/TooltipCard.types';
import { createCursorTooltipPosition } from './createCursorTooltipPosition';
import { type NearestSeriesGroup, type SeriesPointCandidate, findNearestSeriesGroup } from './findNearestSeriesGroup';
import { createSeriesLookup } from './seriesLookup';

type RegisterNearestSeriesTooltipOptions = {
  chart: EChartsType;
  series: TimeSeries[];
  setTooltipCards: (cards: TooltipCard[]) => void;
};

type ZrPointerEvent = {
  offsetX: number;
  offsetY: number;
};

/** The time under the pointer, and the time span the tooltip reaches on either side of it. */
type PointerTimeWindow = {
  timestamp: number;
  fromTimestamp: number;
  toTimestamp: number;
};

function getCoordinate(chart: EChartsType, finder: { gridIndex: number } | { seriesIndex: number }, pixel: number[]) {
  const coordinates = chart.convertFromPixel(finder, pixel);

  return Array.isArray(coordinates) ? coordinates : undefined;
}

function getTimestampAt(chart: EChartsType, offsetX: number, offsetY: number): number | undefined {
  const timestamp = getCoordinate(chart, { gridIndex: PLOT_GRID_INDEX }, [offsetX, offsetY])?.[0];

  return typeof timestamp === 'number' ? timestamp : undefined;
}

function getPointerTimeWindow(chart: EChartsType, { offsetX, offsetY }: ZrPointerEvent): PointerTimeWindow | undefined {
  const timestamp = getTimestampAt(chart, offsetX, offsetY);

  if (timestamp === undefined) {
    return undefined;
  }

  const radius = interactionConfig.tooltipSnapRadiusPx;

  return {
    timestamp,
    fromTimestamp: getTimestampAt(chart, offsetX - radius, offsetY) ?? timestamp,
    toTimestamp: getTimestampAt(chart, offsetX + radius, offsetY) ?? timestamp,
  };
}

/** The pointer's height expressed on the series' own value axis. */
function getValueAtPointer(chart: EChartsType, seriesIndex: number, event: ZrPointerEvent): number | undefined {
  const value = getCoordinate(chart, { seriesIndex }, [event.offsetX, event.offsetY])?.[1];

  return typeof value === 'number' ? value : undefined;
}

/**
 * The real sample the pointer is on. Within the snap radius the one closest to the pointer's height
 * wins: zoomed out, one pixel column holds many samples, and the one nearest in time is usually the
 * baseline next to a spike instead of its peak. Without a sample in reach (a sparse, zoomed-in
 * line), the nearest one in time.
 */
function findPointUnderPointer(
  chart: EChartsType,
  seriesIndex: number,
  { points }: TimeSeries,
  pointer: PointerTimeWindow,
  event: ZrPointerEvent
): number | undefined {
  const value = getValueAtPointer(chart, seriesIndex, event);
  const pointInReach =
    value === undefined
      ? undefined
      : findNearestIndexByValue(points, pointer.fromTimestamp, pointer.toTimestamp, value);

  return pointInReach ?? findNearestIndexByTime(points, pointer.timestamp);
}

/** The pixel height of a sample on its series' own axes; undefined when ECharts cannot place it. */
function getPointY(chart: EChartsType, seriesIndex: number, { timestamp, value }: TimeSeriesPoint): number | undefined {
  const pixel = chart.convertToPixel({ seriesIndex }, [timestamp, value]);

  return Array.isArray(pixel) && typeof pixel[1] === 'number' ? pixel[1] : undefined;
}

function collectCandidates(
  chart: EChartsType,
  seriesByIndex: Map<number, TimeSeries>,
  pointer: PointerTimeWindow,
  event: ZrPointerEvent
): SeriesPointCandidate[] {
  return [...seriesByIndex].flatMap(([seriesIndex, timeSeries]) => {
    const dataIndex = findPointUnderPointer(chart, seriesIndex, timeSeries, pointer, event);
    const pointY = dataIndex === undefined ? undefined : getPointY(chart, seriesIndex, timeSeries.points[dataIndex]);

    return dataIndex === undefined || pointY === undefined ? [] : [{ seriesIndex, dataIndex, pointY }];
  });
}

function buildTooltipCards(group: NearestSeriesGroup, seriesByIndex: Map<number, TimeSeries>): TooltipCard[] {
  return group.flatMap(({ seriesIndex, dataIndex }) => {
    const timeSeries = seriesByIndex.get(seriesIndex);
    const point = timeSeries?.points[dataIndex];

    return timeSeries && point
      ? [
          {
            label: timeSeries.label,
            value: point.value,
            unit: timeSeries.unit,
            color: timeSeries.color,
            timestamp: point.timestamp,
          },
        ]
      : [];
  });
}

/**
 * Shows one tooltip for the series closest to the pointer, with the real sample under the pointer
 * of each series in that group (see findPointUnderPointer). The ZRender listeners fire on every
 * pointer move, so the per-series work (a binary search and a pixel conversion each) is throttled to
 * about one frame and skipped entirely while a drag is in progress: a pan already redraws the chart on the same events, and a
 * tooltip is of no use mid-drag.
 */
export function registerNearestSeriesTooltip({
  chart,
  series,
  setTooltipCards,
}: RegisterNearestSeriesTooltipOptions): () => void {
  const { seriesByIndex, anchorSeriesIndex } = createSeriesLookup(chart, series);
  const zr = chart.getZr();
  let isPointerDown = false;

  const hideTooltip = () => {
    setTooltipCards([]);
    chart.dispatchAction({ type: 'hideTip' });
    chart.dispatchAction({ type: 'updateAxisPointer', currTrigger: 'leave' });
  };

  const showTooltip = (group: NearestSeriesGroup, event: ZrPointerEvent) => {
    setTooltipCards(buildTooltipCards(group, seriesByIndex));

    const [nearest] = group;

    chart.dispatchAction({
      type: 'showTip',
      // The anchor is always resolvable; a raw point index may have been dropped by sampling or
      // filtered out by the viewport, in which case ECharts silently ignores the action.
      seriesIndex: anchorSeriesIndex ?? nearest.seriesIndex,
      dataIndex: anchorSeriesIndex === undefined ? nearest.dataIndex : 0,
      position: createCursorTooltipPosition(chart, event.offsetX, event.offsetY),
    });
  };

  const handleMouseMove = (event: ZrPointerEvent) => {
    if (isPointerDown) {
      return;
    }

    const pointer = [event.offsetX, event.offsetY];

    if (!chart.containPixel({ gridIndex: PLOT_GRID_INDEX }, pointer)) {
      hideTooltip();
      return;
    }

    chart.dispatchAction({
      type: 'updateAxisPointer',
      currTrigger: 'mousemove',
      x: event.offsetX,
      y: event.offsetY,
    });

    const timeWindow = getPointerTimeWindow(chart, event);

    if (!timeWindow) {
      hideTooltip();
      return;
    }

    const candidates = collectCandidates(chart, seriesByIndex, timeWindow, event);
    const group = findNearestSeriesGroup(candidates, event.offsetY, interactionConfig.tooltipGroupingThresholdPx);

    if (group.length === 0) {
      hideTooltip();
      return;
    }

    showTooltip(group, event);
  };

  const throttledHandleMouseMove = throttle(handleMouseMove, interactionConfig.mousemoveThrottleMs, {
    leading: true,
    trailing: true,
  });

  const handlePointerDown = () => {
    isPointerDown = true;
    throttledHandleMouseMove.cancel();
    hideTooltip();
  };

  const handlePointerUp = () => {
    isPointerDown = false;
  };

  const handleGlobalOut = () => {
    isPointerDown = false;
    throttledHandleMouseMove.cancel();
    hideTooltip();
  };

  zr.on('mousemove', throttledHandleMouseMove);
  zr.on('mousedown', handlePointerDown);
  zr.on('mouseup', handlePointerUp);
  zr.on('globalout', handleGlobalOut);

  return () => {
    throttledHandleMouseMove.cancel();
    hideTooltip();

    zr.off('mousemove', throttledHandleMouseMove);
    zr.off('mousedown', handlePointerDown);
    zr.off('mouseup', handlePointerUp);
    zr.off('globalout', handleGlobalOut);
  };
}
